import type {
    GridAssistColumn,
    GridAssistContext,
    GridAssistFilter,
    GridAssistGroupBy,
    GridAssistPlan,
    GridAssistSort,
    GridAssistView,
} from '@/shared/assist/GridAssistContract';
import { GRID_ASSIST_FILTER_OPS } from '@/shared/assist/GridAssistContract';
import { deriveEnumOptions } from './MegaGridDerivation';
import type { MegaGridColumn, MegaGridFilterInstance, MegaGridFilterOp, MegaGridGroupBy, MegaGridView, MegaGridViewState } from './MegaGridTypes';
import type { MegaGridController, MegaGridPatchOptions } from './useMegaGridState';

const SAMPLE_VALUE_CAP = 24;
const VIEW_NAME_CAP = 60;

/**
 * Builds the assist request context from what the grid is currently showing:
 * the effective columns (with inferred filters), the live view state, saved
 * views, and the loaded rows (for enum sample values). This is the model's
 * entire world — the assist can only rearrange what is described here.
 */
export function buildAssistContext<T>(
    columns: MegaGridColumn<T>[],
    state: MegaGridViewState,
    rows: T[],
    views: MegaGridView[],
    activeViewId: string | null,
    title?: string,
): GridAssistContext {
    const hidden = new Set(state.hidden);
    const contextColumns: GridAssistColumn[] = state.columnOrder
        .map((key) => columns.find((c) => c.key === key))
        .filter((c): c is MegaGridColumn<T> => c !== undefined)
        .map((c) => {
            const type = c.filter?.type ?? 'text';
            const sampleValues =
                type === 'enum' ? (c.filter?.options ?? deriveEnumOptions(rows, c)).slice(0, SAMPLE_VALUE_CAP).map((o) => o.value) : undefined;
            return { key: c.key, label: c.header, type, hidden: hidden.has(c.key), sampleValues };
        });
    const contextViews: GridAssistView[] = views.map((v) => ({ id: v.id, name: v.name, active: v.id === activeViewId }));
    return {
        title,
        columns: contextColumns,
        sorts: state.sorts.map((s): GridAssistSort => ({ key: s.key, dir: s.dir })),
        groupBy: state.groupBy.map((g): GridAssistGroupBy => ({ key: g.columnKey, dir: g.sortDir, mode: g.sortMode })),
        filters: state.filters.map(
            (f): GridAssistFilter => ({
                key: f.columnKey,
                op: f.op,
                value: f.value ?? undefined,
                value2: f.value2 ?? undefined,
                values: f.values,
            }),
        ),
        search: state.search,
        density: state.density,
        rowCount: rows.length,
        views: contextViews,
    };
}

function isAssistOp(op: string): op is (typeof GRID_ASSIST_FILTER_OPS)[number] {
    return (GRID_ASSIST_FILTER_OPS as readonly string[]).includes(op);
}

function toFilterInstances(filters: GridAssistFilter[], keySet: Set<string>): MegaGridFilterInstance[] {
    const out: MegaGridFilterInstance[] = [];
    filters.forEach((f, i) => {
        if (!keySet.has(f.key) || !isAssistOp(f.op)) return;
        out.push({
            id: `assist-${String(i + 1)}`,
            columnKey: f.key,
            op: f.op as MegaGridFilterOp,
            value: f.value ?? null,
            value2: f.value2 ?? null,
            values: Array.isArray(f.values) ? f.values.map(String) : undefined,
        });
    });
    return out;
}

/**
 * Translates a validated assist plan into grid mutations, applied as one
 * atomic patch (order: reset/apply-view base → state mutations → save/rename/
 * delete views). Unknown column keys and view ids are dropped silently — the
 * grid never enters an invalid state on imperfect model output.
 */
export function applyAssistPlan<T>(plan: GridAssistPlan, controller: MegaGridController, columns: MegaGridColumn<T>[]): void {
    const allKeys = columns.map((c) => c.key);
    const keySet = new Set(allKeys);

    if (plan.renameView !== undefined) {
        const target = controller.views.find((v) => v.id === plan.renameView?.id);
        const name = plan.renameView.name.trim().slice(0, VIEW_NAME_CAP);
        if (target !== undefined && name.length > 0) controller.renameView(target.id, name);
    }
    if (plan.deleteViewId !== undefined) {
        const target = controller.views.find((v) => v.id === plan.deleteViewId);
        if (target !== undefined) controller.deleteView(target.id);
    }

    const viewBase = plan.applyViewId !== undefined ? controller.views.find((v) => v.id === plan.applyViewId) : undefined;
    const base: MegaGridPatchOptions['base'] = plan.reset === true ? 'default' : (viewBase ?? 'current');
    const baseState: MegaGridViewState =
        plan.reset === true
            ? {
                  search: '',
                  sorts: [],
                  filters: [],
                  facets: {},
                  columnOrder: allKeys,
                  hidden: columns.filter((c) => c.defaultHidden).map((c) => c.key),
                  widths: {},
                  pinned: [],
                  groupBy: [],
                  density: controller.state.density,
              }
            : (viewBase?.state ?? controller.state);

    const patch: Partial<MegaGridViewState> = {};

    if (plan.visibleColumns !== undefined) {
        const visible = plan.visibleColumns.filter((k) => keySet.has(k));
        if (visible.length > 0) {
            patch.hidden = allKeys.filter((k) => !visible.includes(k));
            patch.columnOrder = [...visible, ...allKeys.filter((k) => !visible.includes(k))];
        }
    } else if (plan.showColumns !== undefined || plan.hideColumns !== undefined) {
        const hidden = new Set(baseState.hidden);
        for (const key of plan.showColumns ?? []) hidden.delete(key);
        for (const key of plan.hideColumns ?? []) if (keySet.has(key)) hidden.add(key);
        patch.hidden = [...hidden];
    }

    if (plan.sorts !== undefined) {
        patch.sorts = plan.sorts.filter((s) => keySet.has(s.key) && (s.dir === 'asc' || s.dir === 'desc'));
    }
    if (plan.clearGroupBy === true) {
        patch.groupBy = [];
    } else if (plan.groupBy !== undefined) {
        patch.groupBy = plan.groupBy
            .filter((g) => keySet.has(g.key))
            .map((g): MegaGridGroupBy => ({ columnKey: g.key, sortMode: g.mode ?? 'count', sortDir: g.dir ?? 'desc' }));
    }
    if (plan.clearFilters === true) {
        patch.filters = [];
    } else if (plan.filters !== undefined) {
        patch.filters = toFilterInstances(plan.filters, keySet);
    }
    if (plan.search !== undefined) patch.search = plan.search;
    if (plan.density === 'comfortable' || plan.density === 'compact') patch.density = plan.density;

    const saveAs = plan.saveViewAs?.trim().slice(0, VIEW_NAME_CAP);
    const hasMutation = Object.keys(patch).length > 0;
    if (plan.reset === true || viewBase !== undefined || hasMutation || (saveAs !== undefined && saveAs.length > 0)) {
        controller.applyStatePatch(patch, { base, saveAs: saveAs !== undefined && saveAs.length > 0 ? saveAs : undefined });
    }
}
